/* eslint-disable react/prop-types, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions, jsx-a11y/anchor-is-valid */
import { useQuery } from '@apollo/react-hooks';
import { gql } from 'graphql-tag';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styled, { keyframes, useTheme } from 'styled-components';
import DateCard, { eventDateCardProps } from '../DateCard';
import EventStatusChips from '../EventStatusChips';
import Icon from '../Icon';
import { zoneAbbr } from '../saleTime';
import StatusPill from '../StatusPill';
import { tokens, orange, inkA } from '../tokens';
import useIsMobile from '../useIsMobile';

// Gen-3 EventSnapshotModal — a large read-only "is this event built right?"
// view. Shows the event's sale parameters plus every ticket type (price, sale
// window, sold count, status), filterable by ticket category. Spec source:
// users-accounts.jsx EventSnapshotModal. Data comes from getEvent's admin_*
// ticket fields (the raw build, including hidden/unpublished tickets), unlike
// the live `tickets` field which applies sale-window filtering.

const SNAPSHOT_QUERY = gql`
  query EventSnapshot($input: GetEventInput!) {
    getEvent(input: $input) {
      id
      name
      isMultiDay
      start_date
      end_date
      day
      date
      listDates
      month
      year
      status
      sale_start
      sale_end
      on_sale_time
      off_sale_time
      track {
        id
        name
        timezone
      }
      admin_tickets {
        ...SnapshotTicket
      }
      admin_multiday_tickets {
        ...SnapshotTicket
      }
      admin_bundle_tickets {
        ...SnapshotTicket
        associated_tickets {
          id
          name
          quantity
          price
        }
      }
      admin_other_tickets {
        ...SnapshotTicket
      }
      admin_registration_tickets {
        ...SnapshotTicket
      }
    }
  }

  fragment SnapshotTicket on EventTicket {
    id
    name
    price
    formatted_price
    sale_start
    sale_end
    on_sale_time
    off_sale_time
    limit
    type
    description
    is_soldout
    sold_count
    category_name
    color_code
    visibility
    tiers {
      id
      name
      label
      price
      color_code
    }
    windows {
      id
      name
      ends
      price
      tiers {
        id
        name
        label
        price
        color_code
      }
    }
  }
`;

// Status tones — task spec: soldout=red, scheduled=amber, offsale=gray,
// onsale=green. rgba families match the prototype's CHIP_TONES.
const TT_STATUS = {
  onsale: {
    label: 'On Sale',
    color: '#1F9D57',
    background: 'rgba(52,210,123,0.13)',
    border: 'rgba(52,210,123,0.34)',
  },
  scheduled: {
    label: 'Scheduled',
    color: '#B4790B',
    background: 'rgba(245,158,11,0.15)',
    border: 'rgba(245,158,11,0.36)',
  },
  offsale: {
    label: 'Off Sale',
    color: '#64748b',
    background: 'rgba(100,116,139,0.13)',
    border: 'rgba(100,116,139,0.32)',
  },
  soldout: {
    label: 'Sold Out',
    color: '#D6402F',
    background: 'rgba(240,86,62,0.14)',
    border: 'rgba(240,86,62,0.36)',
  },
};

const pulse = keyframes`
  0% { opacity: 0.55; }
  50% { opacity: 1; }
  100% { opacity: 0.55; }
`;

const SkeletonBar = styled.div`
  height: ${(p) => p.$h || '14px'};
  width: ${(p) => p.$w || '70%'};
  border-radius: ${(p) => p.$r || '7px'};
  background: ${(p) => inkA(p.theme, 0.09)};
  animation: ${pulse} 1.2s ease-in-out infinite;
`;

const COLS = '2.2fr 1.2fr 1fr 0.8fr 1.2fr';

// Parse a MM-DD-YYYY event date (matches DateCard) into a moment.

// Parse a ticket-level sale date. The EventTicket resolvers format these as
// MM-DD-YYYY; fall back to ISO and loose parsing for safety.
const parseTicketDate = (d) => {
  if (!d) return null;
  const exact = moment(d, 'MM-DD-YYYY', true);
  if (exact.isValid()) return exact;
  const iso = moment(d, moment.ISO_8601, true);
  if (iso.isValid()) return iso;
  const loose = moment(new Date(d));
  return loose.isValid() ? loose : null;
};

const fmtDay = (m) => (m ? m.format('MMM D') : '—');

// "23:59:59" -> "11:59 PM". Times are stored as wall-clock strings and are
// shown verbatim — no conversion to the viewer's zone.
const fmtTime = (time) => {
  if (!time) return null;
  const m = moment(time, ['HH:mm:ss', 'HH:mm', 'h:mm A'], true);
  return m.isValid() ? m.format('h:mm A') : null;
};

// One sale boundary: "May 1, 2026 · 11:59 PM CT" (only the parts that exist).
const saleValueLine = (dateText, time, tz) => {
  const timeText = fmtTime(time);
  if (!timeText || dateText === '—') return dateText;
  return `${dateText} · ${timeText}${tz ? ` ${tz}` : ''}`;
};

// The track's IANA zone, preferring the freshly-loaded snapshot over the row
// the modal was opened with.
const trackTimezone = (loaded, event) =>
  (loaded && loaded.track && loaded.track.timezone) ||
  (event.track && event.track.timezone) ||
  null;

// Event-level sale param value. Event.sale_start/sale_end arrive as
// MM-DD-YYYY strings (same format the legacy events pages parse). The design
// shows month + day only, no year.
function fmtEventSale(value) {
  if (!value) return '—';
  const exact = moment(value, 'MM-DD-YYYY', true);
  if (exact.isValid()) return exact.format('MMM D');
  const loose = moment(new Date(value));
  return loose.isValid() ? loose.format('MMM D') : String(value);
}

// Derive a ticket's status. soldout > scheduled > offsale > onsale.
function ticketStatus(ticket) {
  const now = moment();
  const limitReached =
    ticket.limit != null &&
    ticket.limit > 0 &&
    (ticket.sold_count || 0) >= ticket.limit;
  if (ticket.is_soldout || limitReached) return 'soldout';
  const start = parseTicketDate(ticket.sale_start);
  if (start && start.isAfter(now)) return 'scheduled';
  const end = parseTicketDate(ticket.sale_end);
  if (end && end.clone().endOf('day').isBefore(now)) return 'offsale';
  return 'onsale';
}

const CATEGORY_FALLBACK = 'Uncategorized';

// Ticket price for display — formatted server price first, raw price second.
// Every price the ticket can sell at: its base price plus any sale-window
// and tier prices (windows carry their own tiers). Rendered as a range when
// they differ, so tiered/windowed tickets don't masquerade as one price.
const collectPrices = (values) => {
  const prices = [];
  values.forEach((value) => {
    const n = Number(value);
    if (value != null && !Number.isNaN(n)) prices.push(n);
  });
  return prices;
};

const ticketPrices = (tk) =>
  collectPrices(
    [tk.price].concat((tk.tiers || []).map((tier) => tier.price)).concat(
      (tk.windows || []).reduce(
        (acc, w) =>
          acc.concat(
            [w.price],
            (w.tiers || []).map((tier) => tier.price)
          ),
        []
      )
    )
  );

const windowPrices = (w) =>
  collectPrices([w.price].concat((w.tiers || []).map((tier) => tier.price)));

const fmtPrice = (n) => `$${n.toFixed(2)}`;

const priceRangeText = (prices, fallback = '—') => {
  if (!prices.length) return fallback;
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  return min === max ? fmtPrice(min) : `${fmtPrice(min)} – ${fmtPrice(max)}`;
};

const ticketPrice = (tk, fallback) => {
  const prices = ticketPrices(tk);
  if (!prices.length) return tk.formatted_price || fallback;
  if (Math.min(...prices) === Math.max(...prices)) {
    return tk.formatted_price || fmtPrice(prices[0]);
  }
  return priceRangeText(prices, fallback);
};

// Window `ends` arrives as a raw Date scalar (no per-field formatter) — parse
// leniently and show month + day like the rest of the modal.
const fmtWindowEnds = (value) => {
  if (!value) return null;
  const exact = moment(value, 'MM-DD-YYYY', true);
  if (exact.isValid()) return exact.format('MMM D');
  const loose = moment(new Date(value));
  return loose.isValid() ? loose.format('MMM D') : null;
};

const tierLabel = (tier) => tier.label || tier.name || 'Tier';

// Sale-window stat tile.
function Param({ icon, label, value, tone }) {
  const theme = useTheme();
  const t = tokens(theme);
  return (
    <div
      style={{
        flex: 1,
        minWidth: 150,
        background: inkA(theme, 0.025),
        border: '1px solid ' + t.border,
        borderRadius: 12,
        padding: '13px 15px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
        <Icon name={icon} size={14} color={tone || orange(1)} />
        <span
          style={{
            fontSize: 10.5,
            fontWeight: 800,
            letterSpacing: '0.6px',
            textTransform: 'uppercase',
            color: t.faint,
          }}
        >
          {label}
        </span>
      </div>
      <div
        style={{
          fontSize: 14,
          fontWeight: 700,
          color: tone || t.text,
          marginTop: 7,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function HeaderCell({ label }) {
  const theme = useTheme();
  const t = tokens(theme);
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: '0.8px',
        textTransform: 'uppercase',
        color: t.label,
      }}
    >
      {label}
    </div>
  );
}

// Modal header — date card, name, track, event status, close.
function HeaderCloseButton({ onClose }) {
  const theme = useTheme();
  return (
    <button
      type="button"
      onClick={onClose}
      aria-label="Close"
      style={{
        width: 34,
        height: 34,
        flexShrink: 0,
        borderRadius: 9,
        cursor: 'pointer',
        background: inkA(theme, 0.05),
        border: '1px solid ' + inkA(theme, 0.12),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon name="x" size={18} color={inkA(theme, 0.7)} />
    </button>
  );
}

const headerLabelStyle = {
  fontSize: 10,
  fontWeight: 800,
  letterSpacing: '1px',
  textTransform: 'uppercase',
  color: orange(1),
};

// Venue name + event status pill, wrapping as needed.
function HeaderMeta({ event, eventStatus, kind }) {
  const theme = useTheme();
  const t = tokens(theme);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flexWrap: 'wrap',
      }}
    >
      {event.track && event.track.name ? (
        <span style={{ fontSize: 13, color: t.sub }}>{event.track.name}</span>
      ) : null}
      <EventStatusChips
        event={{ ...event, status: eventStatus }}
        kind={kind}
        direction="row"
      />
    </div>
  );
}

// On phones the label and close button take the top row so the event name
// gets the full width beside the date card — long names wrap cleanly
// instead of squeezing into the sliver between date card and close button.
function SnapshotHeaderMobile({
  event,
  eventStatus,
  kind,
  onClose,
  dateProps,
}) {
  const theme = useTheme();
  const t = tokens(theme);
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        padding: '16px 18px',
        borderBottom: '1px solid ' + t.border,
        flexShrink: 0,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <span style={headerLabelStyle}>Event Snapshot</span>
        <HeaderCloseButton onClose={onClose} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {dateProps ? <DateCard {...dateProps} /> : null}
        <h3
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: 19,
            fontWeight: 800,
            color: t.text,
            margin: 0,
            letterSpacing: '-0.3px',
            lineHeight: 1.25,
            overflowWrap: 'anywhere',
          }}
        >
          {event.name}
        </h3>
      </div>
      <HeaderMeta event={event} eventStatus={eventStatus} kind={kind} />
    </div>
  );
}

function SnapshotHeader({ event, eventStatus, kind, onClose }) {
  const theme = useTheme();
  const t = tokens(theme);
  const isMobile = useIsMobile();
  const dateProps = eventDateCardProps(event);

  if (isMobile) {
    return (
      <SnapshotHeaderMobile
        event={event}
        eventStatus={eventStatus}
        kind={kind}
        onClose={onClose}
        dateProps={dateProps}
      />
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        padding: '22px 26px',
        borderBottom: '1px solid ' + t.border,
        flexShrink: 0,
      }}
    >
      {dateProps ? <DateCard {...dateProps} /> : null}
      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={headerLabelStyle}>Event Snapshot</span>
        <h3
          style={{
            fontSize: 22,
            fontWeight: 800,
            color: t.text,
            margin: '4px 0 0',
            letterSpacing: '-0.3px',
          }}
        >
          {event.name}
        </h3>
        <div style={{ marginTop: 9 }}>
          <HeaderMeta event={event} eventStatus={eventStatus} kind={kind} />
        </div>
      </div>
      {event.track && event.track.logo ? (
        <img
          src={event.track.logo}
          alt={event.track.name || ''}
          style={{ height: 46, width: 'auto', flexShrink: 0, display: 'block' }}
        />
      ) : null}
      <HeaderCloseButton onClose={onClose} />
    </div>
  );
}

// Category filter chips. Hidden only when no ticket has a real category —
// an "All / Uncategorized" pair filters nothing.
function CategoryChips({ categories, active, onPick }) {
  const theme = useTheme();
  const t = tokens(theme);
  const real = categories.filter((c) => c !== CATEGORY_FALLBACK);
  if (real.length === 0) return null;
  return (
    <div
      style={{
        display: 'flex',
        gap: 7,
        flexWrap: 'wrap',
        marginLeft: 4,
      }}
    >
      {['all'].concat(categories).map((k) => {
        const on = active === k;
        return (
          <button
            key={k}
            type="button"
            onClick={() => onPick(k)}
            style={{
              height: 30,
              padding: '0 13px',
              borderRadius: 20,
              cursor: 'pointer',
              background: on ? orange(1) : inkA(theme, 0.05),
              border: '1px solid ' + (on ? orange(1) : t.border),
              color: on ? '#fff' : t.sub,
              fontSize: 12.5,
              fontWeight: 700,
            }}
          >
            {k === 'all' ? 'All' : k}
          </button>
        );
      })}
    </div>
  );
}

// Loading placeholder rows for the ticket grid; stacked bars on phones.
function SkeletonRows({ mobile }) {
  const theme = useTheme();
  const t = tokens(theme);
  return Array.from({ length: 5 }).map((_, i) => {
    const border = i === 4 ? 'none' : '1px solid ' + t.line;
    if (mobile) {
      return (
        <div
          key={i}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 9,
            padding: '15px 16px',
            borderBottom: border,
          }}
        >
          <SkeletonBar $w="70%" />
          <SkeletonBar $w="45%" $h="11px" />
          <SkeletonBar $w="60px" $h="22px" $r="999px" />
        </div>
      );
    }
    return (
      <div
        key={i}
        style={{
          display: 'grid',
          gridTemplateColumns: COLS,
          gap: 10,
          alignItems: 'center',
          padding: '15px 16px',
          borderBottom: border,
        }}
      >
        <SkeletonBar $w="70%" />
        <SkeletonBar $w="45%" />
        <SkeletonBar $w="55%" />
        <SkeletonBar $w="55%" />
        <SkeletonBar $w="50%" />
        <SkeletonBar $w="60px" $h="22px" $r="999px" />
      </div>
    );
  });
}

// 30px file-text button opening the per-ticket description sub-modal.
function DescriptionButton({ onClick }) {
  const theme = useTheme();
  const t = tokens(theme);
  return (
    <button
      type="button"
      onClick={onClick}
      title="View description"
      style={{
        width: 30,
        height: 30,
        flexShrink: 0,
        marginLeft: 'auto',
        borderRadius: 8,
        cursor: 'pointer',
        background: inkA(theme, 0.05),
        border: '1px solid ' + t.border,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
      }}
    >
      <Icon name="file-text" size={15} color={t.sub} />
    </button>
  );
}

// Stacked phone card for one ticket — the name gets the full width and
// wraps; price, sold count, and the sale window follow underneath.
// Compact build chips for the mobile card — the same windows/tiers the
// desktop sub-rows show, one chip each.
function MobileBuildChips({ tk }) {
  const theme = useTheme();
  const t = tokens(theme);
  const rows = ticketBuildRows(tk);
  if (!rows.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {rows.map((row) => (
        <span
          key={row.key}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            height: 26,
            padding: '0 10px',
            borderRadius: 8,
            background: inkA(theme, 0.05),
            border: '1px solid ' + t.border,
            fontSize: 12,
            fontWeight: 600,
            color: inkA(theme, 0.8),
            whiteSpace: 'nowrap',
          }}
        >
          {row.dot ? (
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: 3,
                background: row.dot,
                flexShrink: 0,
              }}
            />
          ) : null}
          {row.name}
          {row.price ? (
            <span
              style={{
                fontWeight: 800,
                color: t.text,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {row.price}
            </span>
          ) : null}
          {row.ends ? (
            <span style={{ fontSize: 10.5, color: t.faint }}>
              ends {row.ends}
            </span>
          ) : null}
        </span>
      ))}
    </div>
  );
}

function TicketCardMobile({ tk, last, tz, onShowDetail }) {
  const theme = useTheme();
  const t = tokens(theme);
  const tone = TT_STATUS[ticketStatus(tk)];
  const onSaleM = parseTicketDate(tk.sale_start);
  const offSaleM = parseTicketDate(tk.sale_end);
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 9,
        padding: '14px 16px',
        borderBottom: last ? 'none' : '1px solid ' + t.line,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 10,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 14.5,
              fontWeight: 700,
              color: t.text,
              lineHeight: 1.3,
              overflowWrap: 'anywhere',
            }}
          >
            {tk.name}
          </div>
          <div style={{ fontSize: 11.5, color: t.faint, marginTop: 2 }}>
            {tk.category_name || CATEGORY_FALLBACK}
          </div>
        </div>
        <StatusPill
          label={tone.label}
          color={tone.color}
          background={tone.background}
          border={tone.border}
        />
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          flexWrap: 'wrap',
        }}
      >
        <span
          style={{
            fontSize: 14,
            fontWeight: 800,
            color: t.text,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {ticketPrice(tk, '—')}
        </span>
        {tk.limit != null && tk.limit > 0 ? (
          <span
            style={{
              fontSize: 13,
              color: t.sub,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            Limit {tk.limit}
          </span>
        ) : null}
        {tk.description ? (
          <DescriptionButton onClick={() => onShowDetail(tk)} />
        ) : null}
      </div>
      <MobileBuildChips tk={tk} />
      {onSaleM || offSaleM ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            fontSize: 12,
            color: t.faint,
          }}
        >
          {onSaleM ? (
            <span>
              On sale {saleValueLine(fmtDay(onSaleM), tk.on_sale_time, tz)}
            </span>
          ) : null}
          {offSaleM ? (
            <span>
              Off sale {saleValueLine(fmtDay(offSaleM), tk.off_sale_time, tz)}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// Pass-color swatch beside the ticket name.
function ColorSwatch({ color }) {
  const theme = useTheme();
  return (
    <span
      style={{
        width: 14,
        height: 22,
        borderRadius: 4,
        flexShrink: 0,
        background: color || inkA(theme, 0.12),
        boxShadow: 'inset 0 0 0 1px ' + inkA(theme, 0.18),
      }}
    />
  );
}

// The ticket's sale window as one dates-only cell (times live in the tile row
// above the table).
const saleWindowText = (tk) => {
  const on = fmtDay(parseTicketDate(tk.sale_start));
  const off = fmtDay(parseTicketDate(tk.sale_end));
  if (on !== '—' && off !== '—') return `${on} – ${off}`;
  if (on !== '—') return on;
  if (off !== '—') return `ends ${off}`;
  return '—';
};

// One ticket line in the build grid.
function TicketRow({ tk, last, onShowDetail }) {
  const theme = useTheme();
  const t = tokens(theme);
  const status = ticketStatus(tk);
  const tone = TT_STATUS[status];
  const scheduled = status === 'scheduled';
  const catLabel = tk.category_name || CATEGORY_FALLBACK;
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: COLS,
        gap: 10,
        alignItems: 'center',
        padding: '13px 16px',
        borderBottom: last ? 'none' : '1px solid ' + t.line,
      }}
    >
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}
      >
        <ColorSwatch color={tk.color_code} />
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: t.text,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {tk.name}
          </div>
          <div
            style={{
              fontSize: 11.5,
              color: t.faint,
              marginTop: 2,
            }}
          >
            {catLabel}
          </div>
        </div>
      </div>
      <div
        style={{
          fontSize: 14,
          fontWeight: 800,
          color: t.text,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {ticketPrice(tk, '—')}
      </div>
      <div
        style={{
          fontSize: 13,
          color: scheduled ? orange(1) : t.sub,
          fontWeight: scheduled ? 700 : 500,
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
        }}
      >
        {saleWindowText(tk)}
      </div>
      <div
        style={{
          fontSize: 13.5,
          fontWeight: 700,
          color: tk.limit != null && tk.limit > 0 ? t.text : t.faint,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {tk.limit != null && tk.limit > 0 ? tk.limit : '—'}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <StatusPill
          label={tone.label}
          color={tone.color}
          background={tone.background}
          border={tone.border}
        />
        {tk.description ? (
          <DescriptionButton onClick={() => onShowDetail(tk)} />
        ) : null}
      </div>
    </div>
  );
}

// WINDOW / TIER label on a build sub-row.
function BuildTag({ children }) {
  const theme = useTheme();
  return (
    <span
      style={{
        fontSize: 9.5,
        fontWeight: 800,
        letterSpacing: '0.6px',
        textTransform: 'uppercase',
        color: inkA(theme, 0.38),
        background: inkA(theme, 0.06),
        borderRadius: 5,
        padding: '1px 6px',
        flexShrink: 0,
      }}
    >
      {children}
    </span>
  );
}

// Indented window/tier line under its ticket (B1: plain indent, no glyphs).
// `deep` marks a tier that lives inside a window. `inline` rows (bundle
// contents) put the price right after the name and skip the dashed columns.
function BuildSubRow({ tag, name, dot, price, ends, deep, inline, last }) {
  const theme = useTheme();
  const t = tokens(theme);
  const nameCell = (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        minWidth: 0,
        paddingLeft: deep ? 48 : 27,
      }}
    >
      <BuildTag>{tag}</BuildTag>
      {dot ? (
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: 3,
            flexShrink: 0,
            background: dot,
          }}
        />
      ) : null}
      <span
        style={{
          fontSize: 12.5,
          fontWeight: 600,
          color: inkA(theme, 0.72),
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {name}
      </span>
      {inline && price ? (
        <span
          style={{
            fontSize: 12.5,
            fontWeight: 700,
            color: t.text,
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
          }}
        >
          · {price}
        </span>
      ) : null}
    </div>
  );
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: COLS,
        gap: 10,
        alignItems: 'center',
        padding: '7px 16px',
        borderBottom: last ? 'none' : '1px solid ' + t.line,
        background: inkA(theme, 0.016),
      }}
    >
      {inline ? (
        <>
          <div style={{ gridColumn: '1 / -1', minWidth: 0 }}>{nameCell}</div>
        </>
      ) : (
        <>
          {nameCell}
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: t.text,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {price}
          </div>
          <div style={{ fontSize: 13, color: t.sub, whiteSpace: 'nowrap' }}>
            {ends ? `ends ${ends}` : <span style={{ color: t.faint }}>—</span>}
          </div>
          <div style={{ fontSize: 13, color: t.faint }}>—</div>
          <div />
        </>
      )}
    </div>
  );
}

// Flatten a ticket's windows (with their nested tiers), ticket-level tiers,
// and bundle contents into sub-row descriptors, in build order.
const ticketBuildRows = (tk) => {
  const rows = [];
  (tk.associated_tickets || []).forEach((child, ci) => {
    const childPrices = collectPrices([child.price]);
    rows.push({
      key: `b-${child.id != null ? child.id : ci}`,
      tag: 'Includes',
      // Bundle contents carry no window/limit/status data, so the whole line
      // renders inline in the name cell instead of leaving dashed columns.
      inline: true,
      name: `${child.quantity || 1} × ${child.name || 'Ticket'}`,
      price: childPrices.length ? priceRangeText(childPrices) : null,
    });
  });
  (tk.windows || []).forEach((w, wi) => {
    rows.push({
      key: `w-${w.id != null ? w.id : wi}`,
      tag: 'Window',
      name: w.name || 'Window',
      price: priceRangeText(windowPrices(w)),
      ends: fmtWindowEnds(w.ends),
    });
    (w.tiers || []).forEach((tier, ti) => {
      rows.push({
        key: `w-${w.id != null ? w.id : wi}-t-${
          tier.id != null ? tier.id : ti
        }`,
        tag: 'Tier',
        deep: true,
        name: tierLabel(tier),
        dot: tier.color_code,
        price: priceRangeText(collectPrices([tier.price])),
      });
    });
  });
  (tk.tiers || []).forEach((tier, ti) => {
    rows.push({
      key: `t-${tier.id != null ? tier.id : ti}`,
      tag: 'Tier',
      name: tierLabel(tier),
      dot: tier.color_code,
      price: priceRangeText(collectPrices([tier.price])),
    });
  });
  return rows;
};

// Per-ticket description sub-modal.
function TicketDetailModal({ detail, onClose }) {
  const theme = useTheme();
  const t = tokens(theme);
  return (
    <div
      onClick={onClose}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 5,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 28,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 460,
          background: t.card,
          border: '1px solid ' + t.border,
          borderRadius: 16,
          boxShadow: '0 24px 60px rgba(0,0,0,0.55)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
            padding: '20px 22px',
            borderBottom: '1px solid ' + t.border,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: '0.8px',
                textTransform: 'uppercase',
                color: orange(1),
              }}
            >
              {detail.category_name || CATEGORY_FALLBACK}
            </div>
            <h4
              style={{
                fontSize: 18,
                fontWeight: 800,
                color: t.text,
                margin: '5px 0 0',
              }}
            >
              {detail.name}
            </h4>
            <div
              style={{
                fontSize: 14,
                fontWeight: 800,
                color: t.text,
                marginTop: 4,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {ticketPrice(detail, '')}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 32,
              height: 32,
              flexShrink: 0,
              borderRadius: 9,
              cursor: 'pointer',
              background: inkA(theme, 0.05),
              border: '1px solid ' + inkA(theme, 0.12),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon name="x" size={17} color={inkA(theme, 0.7)} />
          </button>
        </div>
        <div style={{ padding: '18px 22px 22px' }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: '0.8px',
              textTransform: 'uppercase',
              color: t.faint,
              marginBottom: 8,
            }}
          >
            Ticket Description
          </div>
          <p
            style={{
              fontSize: 14.5,
              color: t.sub,
              lineHeight: 1.6,
              margin: 0,
            }}
          >
            {detail.description}
          </p>
        </div>
      </div>
    </div>
  );
}

function EventSnapshotModal({ event, kind, onClose }) {
  const theme = useTheme();
  const t = tokens(theme);
  const history = useHistory();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [cat, setCat] = useState('all');
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (detail) setDetail(null);
        else onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, detail]);

  // Lock the page scroll behind the overlay; restore whatever was there on
  // close (the legacy shell doesn't set body overflow, but don't assume).
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const { data, loading } = useQuery(SNAPSHOT_QUERY, {
    variables: { input: { id: Number(event.id) } },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const loaded = data && data.getEvent;

  // Combine every admin_* ticket variant into one build list, grouped by type
  // so each group keeps its own display order.
  const allTickets = useMemo(() => {
    if (!loaded) return [];
    return [].concat(
      loaded.admin_tickets || [],
      loaded.admin_multiday_tickets || [],
      loaded.admin_bundle_tickets || [],
      loaded.admin_other_tickets || [],
      loaded.admin_registration_tickets || []
    );
  }, [loaded]);

  // Hidden tickets stay out of the list (and therefore out of the category
  // chips) — but they still count toward the Tickets Sold total, since hidden
  // tickets can carry real sales.
  const tickets = useMemo(
    () => allTickets.filter((tk) => tk.visibility !== 'hidden'),
    [allTickets]
  );

  const categories = useMemo(() => {
    const seen = [];
    tickets.forEach((tk) => {
      const name = tk.category_name || CATEGORY_FALLBACK;
      if (!seen.includes(name)) seen.push(name);
    });
    return seen;
  }, [tickets]);

  const shown =
    cat === 'all'
      ? tickets
      : tickets.filter((tk) => (tk.category_name || CATEGORY_FALLBACK) === cat);

  // Context-aware route prefix (MANDATORY per CLAUDE.md).
  const { pathname } = location;
  const routePrefix = pathname.includes('admin-track')
    ? '/admin-track'
    : pathname.includes('admin-employee')
    ? '/admin-employee'
    : '/admin';

  const openManager = () => {
    history.push(`${routePrefix}/events/menu/${event.id}`);
  };

  const totalSold = allTickets.reduce((n, tk) => n + (tk.sold_count || 0), 0);
  const eventStatus = (loaded && loaded.status) || event.status;

  // Track-local zone label ("CT"). All stored dates/times are the track's
  // wall clock, so the label is informational — nothing is converted.
  const tz = zoneAbbr(trackTimezone(loaded, event));
  const saleSource = loaded || event;

  let gridBody;
  if (loading && !loaded) {
    gridBody = <SkeletonRows mobile={isMobile} />;
  } else if (shown.length) {
    gridBody = shown.map((tk, i) => {
      const key = tk.id != null ? tk.id : i;
      const lastTicket = i === shown.length - 1;
      if (isMobile) {
        return (
          <TicketCardMobile
            key={key}
            tk={tk}
            last={lastTicket}
            tz={tz}
            onShowDetail={setDetail}
          />
        );
      }
      const subs = ticketBuildRows(tk);
      return (
        <React.Fragment key={key}>
          <TicketRow
            tk={tk}
            last={lastTicket && subs.length === 0}
            onShowDetail={setDetail}
          />
          {subs.map((sub, j) => (
            <BuildSubRow
              key={sub.key}
              {...sub}
              last={lastTicket && j === subs.length - 1}
            />
          ))}
        </React.Fragment>
      );
    });
  } else {
    gridBody = (
      <div
        style={{
          padding: '34px 16px',
          textAlign: 'center',
          color: t.faint,
          fontSize: 14,
        }}
      >
        No tickets on this event yet.
      </div>
    );
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 150,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: 900,
          maxHeight: 'calc(100vh - 96px)',
          display: 'flex',
          flexDirection: 'column',
          background: t.card,
          border: '1px solid ' + t.border,
          borderRadius: 18,
          boxShadow: '0 24px 80px rgba(0,0,0,0.55)',
          overflow: 'hidden',
        }}
      >
        <SnapshotHeader
          event={event}
          eventStatus={eventStatus}
          kind={kind}
          onClose={onClose}
        />

        {/* Body */}
        <div style={{ padding: '20px 26px', overflowY: 'auto' }}>
          {/* Sale parameters */}
          <div
            style={{
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: '1px',
              textTransform: 'uppercase',
              color: orange(1),
              marginBottom: 11,
            }}
          >
            Sale Window
          </div>
          <div
            style={{
              display: 'flex',
              gap: 12,
              flexWrap: 'wrap',
              marginBottom: 24,
            }}
          >
            <Param
              icon="circle-play"
              label="On Sale"
              value={saleValueLine(
                fmtEventSale(saleSource.sale_start),
                saleSource.on_sale_time,
                tz
              )}
            />
            <Param
              icon="circle-stop"
              label="Off Sale"
              value={saleValueLine(
                fmtEventSale(saleSource.sale_end),
                saleSource.off_sale_time,
                tz
              )}
            />
            <Param
              icon="ticket"
              label="Tickets Sold"
              value={loading && !loaded ? '—' : totalSold.toLocaleString()}
            />
          </div>

          {/* Category filter chips (only when 2+ categories) */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginBottom: 14,
              flexWrap: 'wrap',
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: '1px',
                textTransform: 'uppercase',
                color: orange(1),
              }}
            >
              Ticket Types
            </div>
            <CategoryChips
              categories={categories}
              active={cat}
              onPick={setCat}
            />
          </div>

          {/* Ticket grid */}
          <div
            style={{
              border: '1px solid ' + t.border,
              borderRadius: 12,
              overflow: 'hidden',
            }}
          >
            {isMobile ? null : (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: COLS,
                  gap: 10,
                  alignItems: 'center',
                  padding: '13px 16px',
                  background: t.head,
                  borderBottom: '1px solid ' + t.border,
                }}
              >
                <HeaderCell label="Ticket / Build" />
                <HeaderCell label="Price" />
                <HeaderCell label="Sale Ends" />
                <HeaderCell label="Limit" />
                <HeaderCell label="Status" />
              </div>
            )}

            {gridBody}
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 10,
            padding: '16px 26px',
            borderTop: '1px solid ' + t.border,
            flexShrink: 0,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              height: 42,
              padding: '0 18px',
              borderRadius: 10,
              cursor: 'pointer',
              background: 'transparent',
              border: '1px solid ' + inkA(theme, 0.16),
              color: inkA(theme, 0.82),
              fontSize: 13.5,
              fontWeight: 600,
            }}
          >
            Close
          </button>
          <button
            type="button"
            onClick={openManager}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
              height: 42,
              padding: '0 18px',
              borderRadius: 10,
              cursor: 'pointer',
              background: orange(1),
              border: 'none',
              color: '#fff',
              fontSize: 13.5,
              fontWeight: 700,
            }}
          >
            <Icon name="external-link" size={15} color="#fff" /> Open Event
            Manager
          </button>
        </div>

        {detail ? (
          <TicketDetailModal detail={detail} onClose={() => setDetail(null)} />
        ) : null}
      </div>
    </div>
  );
}

export default EventSnapshotModal;
